import { secondsToHms } from "../../siteWidgets/siteWidgetUtils"

interface SharedAudioPlayerControlsClassNames {
    root: string
    progressContainer: string
    timeline: string
    timeLabels: string
    timeLabel: string
    timeLabelCurrent?: string
    timeLabelTotal?: string
    controlsRow: string
    controlsSpacer?: string
    middleControls?: string
    buttonBase?: string
    forwardButton: string
    playPauseButton: string
    playPauseLoadingModifier?: string
    playPausePlayingModifier?: string
    playPauseStatus?: string
    playPauseStatusPlayingModifier?: string
    backwardButton: string
    speedButton?: string
}

interface SharedAudioPlayerControlsProps {
    isPlaying: boolean
    isLoading?: boolean
    currentTime: number
    duration: number
    playbackSpeed?: number
    onPlayPause: () => any
    onSeek: (time: number) => void
    onForward: () => void
    onBackward: () => void
    onSpeedChange?: () => void
    speedControl?: React.ReactNode
    classNames: SharedAudioPlayerControlsClassNames
    showPlayPauseStatus?: boolean
    timeOrder?: "current-total" | "total-current"
    gradientActiveColor: string
    gradientInactiveColor: string
    timelineFallbackMax?: number
    forwardAriaLabel: string
    backwardAriaLabel: string
    playAriaLabel: string
    pauseAriaLabel: string
    loadingAriaLabel: string
    speedAriaLabel?: string
    progressBeforeControls?: boolean
}

export function SharedAudioPlayerControls(props: SharedAudioPlayerControlsProps) {
    const {
        isPlaying,
        isLoading,
        currentTime,
        duration,
        playbackSpeed,
        onPlayPause,
        onSeek,
        onForward,
        onBackward,
        onSpeedChange,
        speedControl,
        classNames,
        showPlayPauseStatus,
        timeOrder,
        gradientActiveColor,
        gradientInactiveColor,
        timelineFallbackMax,
        forwardAriaLabel,
        backwardAriaLabel,
        playAriaLabel,
        pauseAriaLabel,
        loadingAriaLabel,
        speedAriaLabel,
        progressBeforeControls,
    } = props

    const safeDuration = duration > 0 ? duration : 1
    const timelineMax = duration > 0 ? duration : (timelineFallbackMax || 0)
    const clampedCurrentTime = Math.max(0, Math.min(currentTime, safeDuration))
    const percentage = (clampedCurrentTime / safeDuration) * 100
    const rangeBackground = `linear-gradient(90deg, ${gradientActiveColor} ${percentage}%, ${gradientInactiveColor} ${percentage}%)`

    const playPauseClasses = [classNames.playPauseButton]
    if (isPlaying && classNames.playPausePlayingModifier) {
        playPauseClasses.push(classNames.playPausePlayingModifier)
    }
    if (isLoading && classNames.playPauseLoadingModifier) {
        playPauseClasses.push(classNames.playPauseLoadingModifier)
    }

    const playPauseStatusClasses = [classNames.playPauseStatus]
    if (isPlaying && classNames.playPauseStatusPlayingModifier) {
        playPauseStatusClasses.push(classNames.playPauseStatusPlayingModifier)
    }

    const forwardButtonClasses = [classNames.forwardButton]
    if (classNames.buttonBase) {
        forwardButtonClasses.push(classNames.buttonBase)
    }

    const backwardButtonClasses = [classNames.backwardButton]
    if (classNames.buttonBase) {
        backwardButtonClasses.push(classNames.buttonBase)
    }

    const timeValues = timeOrder === "total-current"
        ? [secondsToHms(duration), secondsToHms(clampedCurrentTime)]
        : [secondsToHms(clampedCurrentTime), secondsToHms(duration)]

    const renderTransportButtons = () => (
        <>
            <button
                className={forwardButtonClasses.join(" ")}
                onClick={onForward}
                aria-label={forwardAriaLabel}
            />

            <button
                className={playPauseClasses.join(" ")}
                onClick={isLoading ? undefined : onPlayPause}
                disabled={!!isLoading}
                aria-label={isLoading ? loadingAriaLabel : (isPlaying ? pauseAriaLabel : playAriaLabel)}
            >
                {showPlayPauseStatus && classNames.playPauseStatus && (
                    <div className={playPauseStatusClasses.join(" ")} />
                )}
            </button>

            <button
                className={backwardButtonClasses.join(" ")}
                onClick={onBackward}
                aria-label={backwardAriaLabel}
            />
        </>
    )

    const controlsSection = (
        <div className={classNames.controlsRow}>
            {classNames.controlsSpacer && <div className={classNames.controlsSpacer} />}

            {classNames.middleControls ? (
                <div className={classNames.middleControls}>
                    {renderTransportButtons()}
                </div>
            ) : renderTransportButtons()}

            {speedControl ? speedControl : (onSpeedChange && classNames.speedButton && (
                <button
                    className={classNames.speedButton}
                    onClick={onSpeedChange}
                    aria-label={speedAriaLabel || ""}
                />
            ))}
        </div>
    )

    const progressSection = (
        <div className={classNames.progressContainer}>
            <input
                type="range"
                dir="ltr"
                className={classNames.timeline}
                min={0}
                max={timelineMax}
                value={clampedCurrentTime}
                onChange={(e: any) => onSeek(Number(e.target.value))}
                style={{ background: rangeBackground }}
            />

            <div className={classNames.timeLabels}>
                <span className={classNames.timeLabelCurrent || classNames.timeLabel}>{timeValues[0]}</span>
                <span className={classNames.timeLabelTotal || classNames.timeLabel}>{timeValues[1]}</span>
            </div>
        </div>
    )

    return (
        <div className={classNames.root}>
            {progressBeforeControls ? progressSection : controlsSection}
            {progressBeforeControls ? controlsSection : progressSection}
        </div>
    )
}
